import { createContext, useCallback, useContext, useState } from "react";
import { crmRepository } from "@/data/crmRepository";
import { statusLabel } from "@/data/seed";

const CrmContext = createContext(null);
const now = () => new Date().toISOString();

export const CrmProvider = ({ children }) => {
  const [data, setData] = useState(() => crmRepository.load());
  const [persona, setPersonaState] = useState(() => crmRepository.getPersona());

  const commit = useCallback((recipe) => {
    setData((previous) => {
      const next = JSON.parse(JSON.stringify(previous));
      recipe(next);
      return crmRepository.save(next);
    });
  }, []);

  const setPersona = (value) => {
    setPersonaState(value);
    crmRepository.setPersona(value);
  };

  const addCompany = (payload) => {
    const id = crmRepository.uid("cmp");
    commit((next) => next.companies.unshift({
      id,
      name: payload.name.trim(),
      industryId: payload.industryId,
      city: payload.city.trim(),
      phone: payload.phone.trim(),
      website: payload.website?.trim() || "",
      address: payload.address?.trim() || "",
      status: payload.status || "do_zadzwonienia",
      source: "reczny",
      reviews: 0,
      rating: 0,
      contactMobile: "brak",
      attempts: 0,
      rejectionReason: "",
      createdAt: now(),
      updatedAt: now(),
    }));
    return id;
  };

  const updateStatus = (companyId, status, reason = "") => {
    commit((next) => {
      const company = next.companies.find((item) => item.id === companyId);
      if (!company) return;
      company.status = status;
      company.rejectionReason = status === "odrzucone" ? reason : "";
      company.updatedAt = now();
      next.activities.unshift({ id: crmRepository.uid("act"), companyId, type: "zmiana_statusu", content: `Status zmieniono na ${statusLabel(status)}${reason ? ` — ${reason}` : ""}`, createdAt: now() });
    });
  };

  const addActivity = (companyId, type, content) => {
    commit((next) => {
      const company = next.companies.find((item) => item.id === companyId);
      if (!company) return;
      if (type === "telefon_proba") company.attempts += 1;
      const targetStatus = type === "telefon_proba" ? "proba_kontaktu" : type === "telefon_rozmowa" ? "rozmowa_odbyta" : type === "audyt" ? "otwieracz_przyjety" : null;
      if (targetStatus) company.status = targetStatus;
      company.updatedAt = now();
      next.activities.unshift({ id: crmRepository.uid("act"), companyId, type, content: content.trim() || "Zapisano aktywność bez dodatkowej notatki.", createdAt: now() });
      if (targetStatus) next.activities.unshift({ id: crmRepository.uid("act"), companyId, type: "zmiana_statusu", content: `Status zmieniono na ${statusLabel(targetStatus)}`, createdAt: now() });
    });
  };

  const addReminder = (companyId, dueAt, content) => {
    commit((next) => next.reminders.unshift({ id: crmRepository.uid("rem"), companyId, dueAt, content: content.trim(), done: false, doneAt: null }));
  };

  const toggleReminder = (reminderId) => {
    commit((next) => {
      const reminder = next.reminders.find((item) => item.id === reminderId);
      if (!reminder) return;
      reminder.done = !reminder.done;
      reminder.doneAt = reminder.done ? now() : null;
    });
  };

  const importCompanies = (rows) => {
    commit((next) => {
      rows.forEach((row) => next.companies.unshift({
        id: crmRepository.uid("cmp"), name: row.name, phone: row.phone || "", city: row.city || "", industryId: row.industryId || next.industries[0].id,
        website: row.website || "", address: "", status: "do_zadzwonienia", source: "reczny", reviews: 0, rating: 0, contactMobile: "brak", attempts: 0, rejectionReason: "", createdAt: now(), updatedAt: now(),
      }));
    });
  };

  const addHook = (industryId, hook) => commit((next) => {
    const industry = next.industries.find((item) => item.id === industryId);
    if (industry) industry.hooks.push(hook.trim());
  });

  const resetDemo = () => setData(crmRepository.reset());

  const value = { data, persona, setPersona, addCompany, updateStatus, addActivity, addReminder, toggleReminder, importCompanies, addHook, resetDemo };
  return <CrmContext.Provider value={value}>{children}</CrmContext.Provider>;
};

export const useCrm = () => {
  const context = useContext(CrmContext);
  if (!context) throw new Error("useCrm musi być użyty wewnątrz CrmProvider");
  return context;
};
